import React, {
  CSSProperties,
  FocusEventHandler,
  forwardRef,
  HTMLAttributes,
  KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { get } from 'lodash';
import { connect } from 'react-redux';
import { FixedSizeList, ListChildComponentProps } from 'react-window';
import { IconChevronDown } from '@rossum/ui/icons/tabler';
import {
  Autocomplete,
  autocompleteClasses,
  Box,
  createFilterOptions,
  inputBaseClasses,
  outlinedInputClasses,
  Paper,
  Popper,
  Stack,
  SvgIcon,
  SxProps,
  TextField,
  Theme,
  Typography,
} from '@rossum/ui/material';
import FakeSelect from '../../components/Datapoint/components/FakeSelect';
import { OnValueChangeOptions } from '../../components/Datapoint/components/Highlighter';
import {
  nextRow as nextRowAction,
  previousRow as previousRowAction,
} from '../../redux/modules/datapoints/actions';
import { openSelectMenu as openSelectMenuAction } from '../../redux/modules/ui/actions';
import { State as ReduxState } from '../../types/state';

const ITEM_SIZE = 30;
const EMPTY_VALUE = '';
const BOX_WIDTH = 350;
const OVER_SCAN_COUNT = 20;

const autocompleteStyles: SxProps<Theme> = {
  [`& .${outlinedInputClasses.root}`]: {
    [`&.${autocompleteClasses.inputRoot}`]: {
      [`&.${inputBaseClasses.root}`]: {
        fontSize: 13,
        paddingLeft: theme => theme.spacing(1),
        paddingRight: '60px',

        [`& .${inputBaseClasses.input}`]: {
          padding: theme =>
            `0 ${theme.spacing(1 / 4)} 0 ${theme.spacing(1 / 4)}`,
          textOverflow: 'ellipsis',
          overflow: 'hidden',
        },
      },
    },
    [`& .${outlinedInputClasses.notchedOutline}`]: {
      border: 'unset !important',
    },
  },
};

const ListBoxRow = (props: ListChildComponentProps) => {
  const { data, index, style } = props;
  const thisItem = data[index];

  return React.cloneElement(thisItem, { style });
};

const OuterElementContext = React.createContext<HTMLAttributes<HTMLDivElement>>(
  {}
);

const OuterElementType = forwardRef<HTMLDivElement>(
  (props: HTMLAttributes<HTMLDivElement>, ref) => {
    const outerProps = React.useContext(OuterElementContext);

    return <div ref={ref} {...props} {...outerProps} />;
  }
);

const ListBoxAdapter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLElement>>(
  (componentProps, ref) => {
    const { children, style, ...rest } = componentProps;
    const itemCount = React.Children.count(children);
    const getListBoxHeight = Math.min(7, itemCount) * ITEM_SIZE;

    // TODO: use VirtualizedListboxFixedSize from ui components
    return (
      <div ref={ref} style={{ ...style }}>
        <OuterElementContext.Provider value={rest}>
          <FixedSizeList
            itemData={children}
            itemSize={ITEM_SIZE}
            height={getListBoxHeight}
            width="100%"
            itemCount={itemCount}
            overscanCount={OVER_SCAN_COUNT}
            innerElementType="ul"
            outerElementType={OuterElementType}
          >
            {ListBoxRow}
          </FixedSizeList>
        </OuterElementContext.Provider>
      </div>
    );
  }
);

const ListBoxPaper = (props: React.HTMLAttributes<HTMLDivElement>) => (
  <Paper
    {...props}
    square
    sx={{
      backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.24), rgba(255, 255, 255, 0.24))`,
    }}
  />
);

const listboxAdapterStyles: SxProps<Theme> = {
  padding: `0 !important`,
  [`& ul`]: {
    margin: 0,
  },
  [`& .${autocompleteClasses.option}`]: {
    fontSize: 13,
    paddingLeft: theme => `${theme.spacing(1)}`,
    height: ITEM_SIZE,
  },
};

type ValueType = {
  label: string;
  value: string;
  struct?: unknown;
};

type OwnProps = {
  onChange: (value: string, options?: OnValueChangeOptions) => void;
  options: Array<ValueType>;
  value: { label: string; value: string; struct?: unknown } | null | undefined;
  style?: CSSProperties;
  active: boolean;
  readOnly: boolean;
  clearable: boolean;
};

type DispatchProps = {
  openSelectMenu: () => void;
  nextRow: () => void;
  previousRow: () => void;
};

type Props = OwnProps & DispatchProps;

const styles = {
  popper: {
    maxWidth: 'fit-content',
  },
};

const CustomPopper = (props: React.ComponentPropsWithRef<typeof Popper>) => {
  return <Popper {...props} style={styles.popper} placement="bottom-start" />;
};

const filterOptions = createFilterOptions<ValueType>({
  stringify: ({ value, label }: ValueType) => `${value} ${label}`,
});

const SelectInputFooter = ({
  options,
  value = null,
  readOnly,
  onChange,
  clearable,
  openSelectMenu,
  nextRow,
  previousRow,
}: Props) => {
  const [activeValue, setActiveValue] = useState(value);

  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const [highlightedOption, setHighlightedOption] = useState<ValueType | null>(
    null
  );

  const textFieldInputRef = useRef<HTMLInputElement | null>(null);
  const autocompleteRef = useRef<HTMLDivElement | null>(null);

  const mutableOptions = options;

  useEffect(() => {
    setActiveValue(value);
  }, [value]);

  const handleOpenState = useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        openSelectMenu();
        textFieldInputRef.current?.focus();
      } else {
        textFieldInputRef.current?.blur();
      }

      setAutocompleteOpen(isOpen);
    },
    [openSelectMenu]
  );

  const onValueChange = useCallback(
    (newValue: ValueType) => {
      onChange(newValue?.value || '', {
        setHumanValidationSource: true,
        struct: newValue?.struct,
      });
    },
    [onChange]
  );

  const textFieldInputRefCallback = useCallback((node: HTMLInputElement) => {
    textFieldInputRef.current = node;

    if (node) {
      node?.focus();
    }
  }, []);

  const textFieldInputOnFocus: FocusEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = event => {
    // timeout workaround is related to this issue
    // https://stackoverflow.com/questions/6003300/how-to-place-cursor-at-end-of-text-in-textarea-when-tabbed-into/6003829#6003829
    setTimeout(() => event.target.setSelectionRange(0, 0), 0);
  };

  useEffect(() => {
    const onKeyPress = (event: KeyboardEvent): void => {
      if (['Enter', 'Tab'].includes(event.key)) {
        handleOpenState(true);
      }
      if (['Delete', 'Backspace'].includes(event.key)) {
        event.stopPropagation();

        if (clearable) {
          onValueChange({
            label: EMPTY_VALUE,
            value: EMPTY_VALUE,
            struct: {},
          });
        }
      }
    };

    window.addEventListener('keydown', onKeyPress);

    return () => {
      window.removeEventListener('keydown', onKeyPress);
    };
  }, [clearable, handleOpenState, onValueChange]);

  const onKeyDownHandler = (event: ReactKeyboardEvent) => {
    if (['ArrowDown'].includes(event.key) && !autocompleteOpen) {
      event.stopPropagation();

      textFieldInputRef.current?.blur();
      nextRow();
    }

    if (['ArrowUp'].includes(event.key) && !autocompleteOpen) {
      event.stopPropagation();

      textFieldInputRef.current?.blur();
      previousRow();
    }

    if (event.key === 'Tab' && autocompleteOpen) {
      if (highlightedOption) {
        onValueChange(highlightedOption);
      }
    }

    if (event.key === 'ArrowRight' && autocompleteOpen) {
      handleOpenState(false);
    }
  };

  const resetActiveValue = () => {
    // activeValue.value is empty only in case of new search hint without selected option
    if (activeValue && activeValue.value.length < 1) {
      setActiveValue(value);
    }
  };

  const handleActiveValue = (
    newTypedValue: string,
    previousLabel: string | number | readonly string[] | undefined
  ) => {
    const hasInitialValue = String(previousLabel) === value?.label;

    setActiveValue({
      label: hasInitialValue
        ? newTypedValue.replace(String(previousLabel), '')
        : newTypedValue,
      value: EMPTY_VALUE,
    });
  };

  if (readOnly) {
    return (
      <Stack
        sx={{
          flex: 1,
          whiteSpace: 'pre',
        }}
      >
        {get(value, 'label', null)}
      </Stack>
    );
  }

  return (
    <Box sx={{ background: 'transparent', width: '100%' }}>
      <Autocomplete
        autoHighlight
        PopperComponent={CustomPopper}
        PaperComponent={ListBoxPaper}
        id="react-select"
        className="sidebar-select react-select"
        disableClearable={!clearable}
        open={autocompleteOpen}
        filterOptions={filterOptions}
        onOpen={() => handleOpenState(true)}
        onClose={() => handleOpenState(false)}
        options={mutableOptions}
        renderInput={params => {
          return (
            <TextField
              {...params}
              inputRef={textFieldInputRefCallback}
              onFocus={textFieldInputOnFocus}
              onBlur={resetActiveValue}
              inputProps={{
                ...params.inputProps,
                onKeyDown: onKeyDownHandler,
              }}
              onChange={newValue => {
                handleActiveValue(
                  newValue.target.value,
                  params.inputProps.value
                );
              }}
            />
          );
        }}
        onHighlightChange={(_e, option: ValueType | null) => {
          return setHighlightedOption(option);
        }}
        onChange={(_e, newValue) => onValueChange(newValue as ValueType)}
        value={value}
        inputValue={activeValue?.label ?? ''}
        popupIcon={
          <SvgIcon fontSize="medium">
            <IconChevronDown />
          </SvgIcon>
        }
        ListboxComponent={ListBoxAdapter}
        ListboxProps={{
          sx: {
            ...listboxAdapterStyles,
            height: 'auto !important',
            ul: {
              padding: 0,
              width: BOX_WIDTH,
              overflow: 'auto',
            },
          },
        }}
        slotProps={{
          paper: {
            elevation: 12,
            sx: {
              width: BOX_WIDTH,
            },
          },
        }}
        renderOption={({ key, ...props }, option: ValueType) => {
          const label =
            !option.label || option.label.length === 0
              ? option.value
              : option.label;

          return (
            <Typography
              key={key}
              component="li"
              variant="caption"
              {...props}
              sx={{
                whiteSpace: 'pre',
                // !important is mandatory due override calculated inline styles
                width: 'fit-content !important',
                minWidth: '100%',
              }}
            >
              {label}
            </Typography>
          );
        }}
        ref={autocompleteRef}
        sx={autocompleteStyles}
      />
    </Box>
  );
};

const mapDispatchToProps = {
  openSelectMenu: openSelectMenuAction,
  nextRow: nextRowAction,
  previousRow: previousRowAction,
};

const Connected = connect<unknown, DispatchProps, OwnProps, ReduxState>(
  null,
  mapDispatchToProps
)(SelectInputFooter);

export default (props: OwnProps) =>
  props.active ? (
    <Connected {...props} />
  ) : (
    <FakeSelect
      style={props.style}
      value={get(props.value, 'label')}
      inFooter
    />
  );
